import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useVibesites } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { classifyPhoto, type GalleryImage } from './_galleryShared';

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const photo0Def: SlotGroupDefinition = {
  label: 'Photo 1 (top left)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo1Def: SlotGroupDefinition = {
  label: 'Photo 2 (top right)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Photo 3 (bottom left)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Photo 4 (bottom right)',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const STATIC_PHOTO_DEFS = [photo0Def, photo1Def, photo2Def, photo3Def] as const;

// ============================================================================
// Default photo selection
// ============================================================================

/** Re-point a static photo def at a different gallery index, keeping its label. */
function withGalleryIndex(def: SlotGroupDefinition, index: number): SlotGroupDefinition {
  return {
    ...def,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: { collection: 'photo_gallery', defaultIndex: index, fieldMappings: { image: 'image', alt: 'alt_text' } },
  };
}

/** Default to community-titled gallery photos, topping up with any other photos. */
function pickCommunityIndices(gallery: GalleryImage[], count: number): number[] {
  const withImage = gallery
    .map((photo, index) => ({ photo, index }))
    .filter(({ photo }) => Boolean(photo.image));
  const community = withImage.filter(({ photo }) => classifyPhoto(photo) === 'community');
  const rest = withImage.filter(({ photo }) => classifyPhoto(photo) !== 'community');
  const picked = [...community, ...rest].slice(0, count).map(({ index }) => index);
  while (picked.length < count) picked.push(picked.length);
  return picked;
}

// ============================================================================
// Component
// ============================================================================

export function NeighborhoodSection() {
  const { property } = useVibesites();
  const gallery = property?.photo_gallery as GalleryImage[] | undefined;

  const [def0, def1, def2, def3] = useMemo(() => {
    const indices = pickCommunityIndices(gallery ?? [], STATIC_PHOTO_DEFS.length);
    return STATIC_PHOTO_DEFS.map((def, i) => withGalleryIndex(def, indices[i]));
  }, [gallery]);

  const photo0 = useSlotGroup('neighborhoodsection.photo_0', def0);
  const photo1 = useSlotGroup('neighborhoodsection.photo_1', def1);
  const photo2 = useSlotGroup('neighborhoodsection.photo_2', def2);
  const photo3 = useSlotGroup('neighborhoodsection.photo_3', def3);

  const cta = useSlotGroup('neighborhoodsection.cta_link', ctaLinkDef);

  useRevealStyles();
  useCtaSheenStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full overflow-hidden bg-primary py-10 md:py-12 lg:py-14"
    >
      <div className="mx-auto flex flex-col items-start gap-8 px-4 sm:px-6 lg:grid lg:max-w-none lg:grid-cols-2 lg:items-start lg:gap-6 lg:px-0 xl:gap-10">
        {/* Left: copy on white panel — bleeds to viewport left, ends at center on lg+ */}
        <div className="relative z-10 -mx-4 flex w-[calc(100%+2rem)] flex-col bg-white px-8 py-8 sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-10 sm:py-10 md:px-12 md:py-12 lg:mx-0 lg:w-full lg:px-12 lg:pr-14 xl:px-16 xl:pr-20">
          <RichTextContent
              path="neighborhoodsection.eyebrow"
              label="Eyebrow"
              defaultValue="<p>Ideally Located</p>"
              as="p"
              className="font-body mb-4 text-xs uppercase tracking-[0.25em] text-foreground sm:text-sm"
            />

            <RichTextContent
              path="neighborhoodsection.heading"
              label="Heading"
              defaultValue='<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>'
              as="h2"
              className="font-heading mb-6 text-3xl font-bold uppercase leading-[1.1] text-foreground sm:text-4xl md:text-[2.75rem]"
            />

            <div className="mb-8 h-1 w-16 rounded-full bg-secondary" aria-hidden="true" />

            <RichTextContent
              path="neighborhoodsection.body"
              label="Body Text"
              defaultField="property_home_neighborhood_blurb"
              defaultValue="<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>"
              as="div"
              className="font-body mb-10 max-w-xl text-base leading-relaxed text-muted-foreground [&_a]:text-primary [&_a]:underline"
            />

            <EditableSlotGroup
              groupPath="neighborhoodsection.cta_link"
              definition={ctaLinkDef}
              className="inline-block"
            >
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="neighborhood-cta vantage-cta-sheen inline-flex items-center justify-center px-8 py-3.5 font-heading text-sm uppercase tracking-wider"
              >
                <span className="relative z-10">
                  <RichTextContent
                    path="neighborhoodsection.cta_label"
                    label="CTA Label"
                    defaultValue="<p>Explore the Neighborhood</p>"
                    as="span"
                  />
                </span>
              </a>
          </EditableSlotGroup>
        </div>

        {/* Right: 2×2 photo grid — each cell is an individually picked image */}
        <div className="neighborhood-photo-grid relative z-0 grid w-full max-w-xl grid-cols-2 gap-2.5 sm:max-w-none sm:gap-3 lg:max-w-none lg:pl-4 lg:pr-8 xl:pr-12">
          <EditableSlotGroup groupPath="neighborhoodsection.photo_0" definition={def0} className="neighborhood-grid-cell">
            <VibesitesImage
              url={photo0.image || ''}
              alt={photo0.alt || 'Community photo'}
              className="neighborhood-grid-cell__img"
              sizes="(min-width: 1024px) 25vw, 50vw"
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.photo_1" definition={def1} className="neighborhood-grid-cell">
            <VibesitesImage
              url={photo1.image || ''}
              alt={photo1.alt || 'Community photo'}
              className="neighborhood-grid-cell__img"
              sizes="(min-width: 1024px) 25vw, 50vw"
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.photo_2" definition={def2} className="neighborhood-grid-cell">
            <VibesitesImage
              url={photo2.image || ''}
              alt={photo2.alt || 'Community photo'}
              className="neighborhood-grid-cell__img"
              sizes="(min-width: 1024px) 25vw, 50vw"
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="neighborhoodsection.photo_3" definition={def3} className="neighborhood-grid-cell">
            <VibesitesImage
              url={photo3.image || ''}
              alt={photo3.alt || 'Community photo'}
              className="neighborhood-grid-cell__img"
              sizes="(min-width: 1024px) 25vw, 50vw"
            />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .neighborhood-cta {
          --vantage-cta-sheen-accent: var(--vibe-color-primary);
          --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-primary);
        }

        .neighborhood-cta [data-vibesite-richtext],
        .neighborhood-cta [data-vibesite-richtext] p,
        .neighborhood-cta [data-vibesite-richtext] span {
          margin: 0;
          font: inherit;
          color: inherit;
          text-transform: inherit;
          letter-spacing: inherit;
        }

        .neighborhood-grid-cell {
          position: relative;
          display: block;
          aspect-ratio: 1 / 1;
          width: 100%;
          overflow: hidden;
          background: #fff;
          border-radius: 4px;
        }
        .neighborhood-grid-cell__img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodSection',
  displayName: 'Neighborhood Section',
  tokens: {
    'neighborhoodsection.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Ideally Located</p>',
    },
    'neighborhoodsection.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'neighborhoodsection.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p>Experience the best of downtown living with easy access to dining, entertainment, and cultural destinations. Our prime location puts you at the center of it all while offering a peaceful retreat to call home.</p>',
    },
    'neighborhoodsection.cta_label': {
      label: 'CTA Label',
      type: 'richtext',
      defaultValue: '<p>Explore the Neighborhood</p>',
    },
  },
  slotGroups: {
    'neighborhoodsection.cta_link': ctaLinkDef,
    'neighborhoodsection.photo_0': photo0Def,
    'neighborhoodsection.photo_1': photo1Def,
    'neighborhoodsection.photo_2': photo2Def,
    'neighborhoodsection.photo_3': photo3Def,
  },
};

(NeighborhoodSection as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NeighborhoodSection;
